import { useTranslation } from 'react-i18next';
import type { ChatMessage } from '../../interfaces/chat';
import { isDebugMode } from '../../config/environments';
import { Markdown } from '../Markdown';
import { ToolCallFooter } from '../ToolCallFooter';
import { CitationChips } from '../CitationChips';
import { HaltPrompt } from '../HaltPrompt';
import { DataSourceWarning } from '../DataSourceWarning';
import { OrderHeaderCard } from '../OrderHeaderCard';
import { InsightsList } from '../InsightsList';
import { RootCauseBlock } from '../RootCauseBlock';
import { ResolutionOptionsGroup } from '../ResolutionOptionsGroup';
import { NoViableOptionNotice } from '../NoViableOptionNotice';
import { OrderInsightFooterActions } from '../OrderInsightFooterActions';
import { ContentBlockRenderer } from '../ContentBlocks';
import styles from './MessageBubble.module.css';

interface MessageBubbleProps {
  message: ChatMessage;
  /** Disable halt affordances while a turn is streaming. */
  haltDisabled?: boolean;
  onRespondHalt: (text: string) => void;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function MessageBubble({ message, haltDisabled, onRespondHalt }: MessageBubbleProps) {
  const { t } = useTranslation();
  const isUser = message.role === 'user';

  return (
    <div className={`${styles.row} ${isUser ? styles.rowUser : styles.rowAgent}`}>
      <div className={styles.column}>
        {!isUser ? (
          <div className={styles.meta}>
            <span>{t('ChatbotUI.MessageFromAssistant')}</span>
            <time dateTime={message.timestamp.toISOString()}>{formatTime(message.timestamp)}</time>
          </div>
        ) : null}

        <div className={`${styles.bubble} ${isUser ? styles.bubbleUser : styles.bubbleAgent}`}>
          {isUser ? (
            <span className={styles.userText}>{message.content}</span>
          ) : message.contentBlocks?.length ? (
            <div className={styles.blockStack}>
              {message.contentBlocks.map((block, idx) => (
                <ContentBlockRenderer key={idx} block={block} />
              ))}
            </div>
          ) : (
            <Markdown content={message.content} />
          )}

          {!isUser && isDebugMode() && message.toolCalls?.length ? (
            <ToolCallFooter toolCalls={message.toolCalls} />
          ) : null}

          {!isUser && message.citations?.length ? (
            <CitationChips citations={message.citations} />
          ) : null}

          {!isUser && message.orderInsight ? (
            <>
              <DataSourceWarning dataSources={message.orderInsight.dataSources} />
              <OrderHeaderCard
                orderId={message.orderInsight.orderId}
                health={message.orderInsight.health}
                order={message.orderInsight.order}
              />
              <InsightsList insights={message.orderInsight.insights} />
              <RootCauseBlock rootCauses={message.orderInsight.rootCauses} />
              {message.orderInsight.recommendations.length > 0 ? (
                <ResolutionOptionsGroup recommendations={message.orderInsight.recommendations} />
              ) : (
                <NoViableOptionNotice escalateToCsr={message.orderInsight.actions.escalateToCsr} />
              )}
              <OrderInsightFooterActions actions={message.orderInsight.actions} />
            </>
          ) : null}
        </div>

        {!isUser && message.halt ? (
          <HaltPrompt halt={message.halt} disabled={haltDisabled} onRespond={onRespondHalt} />
        ) : null}

        {!isUser ? (
          null
        ) : (
          <div className={styles.meta}>
            <span>{t('ChatbotUI.MessageFromYou')}</span>
            <time dateTime={message.timestamp.toISOString()}>{formatTime(message.timestamp)}</time>
          </div>
        )}
      </div>
    </div>
  );
}
